
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import BaseInput from '@/components/BaseInput.vue'
import BulkScanSwitchButton from '@/components/common/BulkScanSwitchButton.vue'
import ScannedValueInput from '@/components/common/ScannedValueInput.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeHandlerMixin, { BarcodeParseResult } from '@/components/mixins/BarcodeHandlerMixin.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconInfo from '@/components/svg/IconInfo.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { SAVE_SCANNED_WORK_CENTER_CODE, UPDATE_SCANNED_RESULT } from '@/store/actions'
import { EBCRType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { openMessage, promptVideoPermission } from '@/utils/helpers'
import { TError } from '@/utils/types/commonTypes'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { Options, mixins } from 'vue-class-component'
@Options({
  components: {
    IconInfo,
    ScanArea,
    BarcodeSymbologiesSelection,
    BaseInput,
    BulkScanSwitchButton,
    ScannedValueInput,
    IconSwitch,
    SelectionCameraPopup,
  },
  name: 'ScanView',
})
export default class ScanView extends mixins(BarcodeReaderMixin, BarcodeHandlerMixin) {
  _barcodeDataCapture: BarcodeCapture | undefined
  codeReader = new BrowserMultiFormatReader()
  isReadyToScan = false
  isNavigated = false
  video: HTMLVideoElement | null = null
  listBlob: Blob[] = []

  async defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.isReadyToScan = true
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    this.isNavigated = false
    await this.handleScanResult((temp.text as string) || '')
    if (!this.isMultipleScan && this.isNavigated) {
      codeReader.reset()
    }
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    this.isNavigated = false
    await this.handleScanResult((result[0].rawValue as string) || '')
    if (!this.isMultipleScan && this.isNavigated) {
      this.vidOff()
    }
  }

  async scanditDataResult(symbology: Barcode) {
    this.isReadyToScan = true
    if (!symbology) return

    await this.handleScanResult(symbology.data || '')
  }

  get isAuth() {
    return this.$store.getters.isAuth
  }

  async mounted() {
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false
    await this.initCameraSelection()
    await this.setupCameraSetting()
  }

  async setupCameraSetting() {
    try {
      this.isUserAutheticated()
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  resetScannedValue() {
    this.listBlob = []
    this.valueCode = ''
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  redirectHome() {
    this.$router.replace({
      name: 'home',
    })
  }

  isUserAutheticated() {
    const isRestrictedPage = this.$route.meta.restricted
    if (!this.isAuth && isRestrictedPage) {
      this.redirectHome()
    }
  }

  async handleCompleteScan() {
    if (!this.isMultipleScan || this.valueCode.trim() === '') {
      return
    }
    this.processScannedText(this.valueCode)
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    }
  }

  async snapshot() {
    try {
      const tempCanvas = document.querySelector('#captureCanvas') as HTMLCanvasElement
      const video = document.querySelector('video')
      if (!video || !tempCanvas) return
      const originalWidth = tempCanvas.width
      const originalHeight = tempCanvas.height
      tempCanvas.width = video.videoWidth
      tempCanvas.height = video.videoHeight
      const context = tempCanvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const blob = await new Promise((resolve) => tempCanvas.toBlob(resolve))
      return blob
    } catch (error) {
      console.error('Error during snapshot:', error)
    }
  }

  handleSaveScannedInfo(blob: Blob) {
    this.listBlob.push(blob)
    this.$store.dispatch(UPDATE_SCANNED_RESULT, this.listBlob)
  }

  async handleScanResult(plainText: string) {
    this.isMultipleScan ? await this.handleMultipleScan(plainText) : await this.handleSingleScan(plainText)
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }
    const blob = await this.snapshot()
    this.isHandleMultipleScan = true
    this.$confirm(this.$t('confirm_scanned_value', { item: plainText }), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
        this.handleSaveScannedInfo(blob as Blob)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  handleBarcodeResult(result: BarcodeParseResult) {
    if (result.isNewFormat || result.isOldFormat) {
      this.$store.dispatch(SAVE_SCANNED_WORK_CENTER_CODE, '')
      this.isNavigated = true
      const params = result.isNewFormat
        ? { barcodeId: result.barcodeId }
        : { barcodeId: result.barcodeId, project: result.projectId }
      this.$router.push({ name: 'detail', params, query: { openWorkCenter: 'true' } })
    } else {
      const readingError = `${this.$t('reading_error1')}${this.$t('reading_error2')}${this.$t('reading_error3')}`
      openMessage(readingError, 'warning')
    }
  }

  async handleSingleScan(plainText: string) {
    const blob = await this.snapshot()
    this.handleSaveScannedInfo(blob as Blob)
    this.valueCode = plainText
    this.processScannedText(plainText)
  }
}
